import {
  useState,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  ReactNode,
} from "react";
import { cn } from "@/lib/utils";

type PercentageInputProps = {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  step?: number;
  max?: number;
  leadingComponent?: ReactNode;
};

export function PercentageInput({
  value,
  onChange,
  className,
  placeholder = "100",
  step = 10,
  max = 100,
  leadingComponent,
}: PercentageInputProps) {
  const [inputValue, setInputValue] = useState(String(value));

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const submit = () => {
    const isValid = /^\d+$/.test(inputValue);

    if (!isValid) {
      setInputValue(String(value));
      return;
    }

    const num = parseInt(inputValue, 10);
    const clamped = Math.min(num, max);
    setInputValue(String(clamped));
    onChange(clamped);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (val === "") {
      setInputValue(val);
      return;
    }

    const isNumber = /^\d+$/.test(val);
    if (!isNumber) {
      return;
    }

    const num = parseInt(val, 10);
    if (num >= 0 && num <= max) {
      setInputValue(val);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      submit();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const current = parseInt(inputValue, 10) || 0;
      const newValue = Math.min(current + step, max);
      setInputValue(String(newValue));
      onChange(newValue);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const current = parseInt(inputValue, 10) || 0;
      const newValue = Math.max(current - step, 0);
      setInputValue(String(newValue));
      onChange(newValue);
    }
  };

  return (
    <div className={cn("relative w-20", className)}>
      {leadingComponent && (
        <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {leadingComponent}
        </div>
      )}
      <input
        className={cn(
          "h-9 w-full rounded-md border border-input bg-background py-2 pr-7 text-sm placeholder:text-muted-foreground",
          leadingComponent ? "pl-10" : "pl-3",
        )}
        value={inputValue}
        onChange={handleChange}
        onBlur={submit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
      <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">
        %
      </span>
    </div>
  );
}
